import { useState, useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  ChatProvider,
  ChatWindow,
  ConversationList,
  requestTherapistToken,
  requestPatientToken,
  requestAgencyToken,
  createConversation,
  extractTherapistId,
  extractPatientId,
  resolveTwilioConversationSid,
  isTwilioConversationSid,
} from '@/chat'
import type {
  ChatConversation,
  PatientInfo,
  TherapistInfo,
} from '@/chat/services/chatApi'
import { UserRole } from '@/types'
import { useAppSelector } from '@/store'
import axios from 'axios'

const getEntityDisplayName = (entity: unknown, fallback: string): string => {
  if (!entity || typeof entity === 'string') return fallback

  const typed = entity as PatientInfo | TherapistInfo | { name?: string; agencyName?: string }
  // Agency objects may use `agencyName`
  if ('agencyName' in (typed as any) && (typed as any).agencyName) {
    return (typed as any).agencyName
  }

  const first = (typed as PatientInfo | TherapistInfo).firstName?.trim() ?? ''
  const last = (typed as PatientInfo | TherapistInfo).lastName?.trim() ?? ''
  const name = (typed as { name?: string }).name?.trim() ?? ''

  const combined = `${first || name} ${last}`.trim()
  return combined || fallback
}

const getEntityAvatarUrl = (entity: unknown): string | undefined => {
  if (!entity || typeof entity === 'string') return undefined
  const typed = entity as
    | PatientInfo
    | TherapistInfo
    | {
      avatarUrl?: string
      avatar?: string
      profilePhotoUrl?: string
    }
  return (
    typed.profilePhotoUrl ??
    ('avatarUrl' in typed ? typed.avatarUrl : undefined) ??
    ('avatar' in typed ? typed.avatar : undefined)
  )
}

const getCurrentUserDisplayName = (user?: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  id?: string | null
}): string => {
  if (!user) return 'You'
  const first = user.firstName?.trim() ?? ''
  const last = user.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  if (user.email) return user.email
  if (user.id) return user.id
  return 'You'
}

const MessagingPage = () => {
  const theme = useTheme()
  const { user } = useAppSelector(state => state.auth)
  const [selectedConversation, setSelectedConversation] =
    useState<ChatConversation | null>(null)

  const currentUserId = user?.id ?? ''
  const role = user?.role ?? null
  const isTherapist = role === UserRole.THERAPIST
  let participantFallbackLabel = isTherapist ? 'Patient' : 'Provider'

  const participantEntity = selectedConversation
    ? role === UserRole.AGENCY
      ? // If agency, prefer the tab the user selected when opening the conversation
      ((selectedConversation as any)._selectedAgencyTab === 'providers'
        ? selectedConversation.therapistId
        : selectedConversation.patientId)
      : isTherapist
        ? ((selectedConversation as any)._selectedTherapistTab === 'agencies'
          ? selectedConversation.agencyId
          : selectedConversation.patientId)
        : // Patient: if they opened an agency tab, prefer agencyId as participant
        ((selectedConversation as any)._selectedPatientTab === 'agencies'
          ? selectedConversation.agencyId
          : selectedConversation.therapistId)
    : null

  // If patient or therapist opened an agency conversation, use 'Agency' as the fallback label
  if (selectedConversation && !isTherapist && (selectedConversation as any)._selectedPatientTab === 'agencies') {
    participantFallbackLabel = 'Agency'
  } else if (selectedConversation && isTherapist && (selectedConversation as any)._selectedTherapistTab === 'agencies') {
    participantFallbackLabel = 'Agency'
  }

  const isParticipantDeleted = !participantEntity

  const participantDisplayName = isParticipantDeleted
    ? 'Deleted user'
    : getEntityDisplayName(participantEntity, participantFallbackLabel)

  const participantAvatarUrl = isParticipantDeleted
    ? undefined
    : getEntityAvatarUrl(participantEntity)

  const currentUserDisplayName = getCurrentUserDisplayName(user ?? undefined)

  const tokenFetcher = useMemo(() => {
    if (!currentUserId) return undefined

    // If current user is an agency, resolve agency id and fetch agency token
    if (role === UserRole.AGENCY) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const u = user as any
      const agencyId =
        u?.agencyId || (typeof u?.agency === 'string' ? u?.agency : u?.agency?._id) || currentUserId
      return (signal?: AbortSignal) => requestAgencyToken(agencyId, signal)
    }

    return isTherapist
      ? (signal?: AbortSignal) => requestTherapistToken(currentUserId, signal)
      : (signal?: AbortSignal) => requestPatientToken(currentUserId, signal)
  }, [currentUserId, isTherapist, role, user])

  const conversationResolver = useMemo(() => {
    if (!selectedConversation || !currentUserId) return undefined

    const therapistId = extractTherapistId(selectedConversation.therapistId)
    const patientId = extractPatientId(selectedConversation.patientId)
    const existingConversationSid = resolveTwilioConversationSid(
      selectedConversation.conversationSid,
      selectedConversation.conversationId
    )
    const hasValidTwilioSid = isTwilioConversationSid(existingConversationSid)

    return async (signal?: AbortSignal): Promise<string> => {
      try {
        if (!therapistId || !patientId) {
          throw new Error('Invalid therapist/patient identifiers for chat.')
        }

        // Always resolve canonical conversation SID from backend.
        // This prevents stale list data from producing Twilio "Not Found".
        const conversation = await createConversation(
          {
            therapistId,
            patientId,
          },
          undefined
        )

        const sid = resolveTwilioConversationSid(
          conversation.conversationSid,
          conversation.conversationId
        )
        if (!sid) {
          throw new Error(
            'Conversation SID missing from server response. Response: ' +
            JSON.stringify(conversation)
          )
        }
        if (!sid.startsWith('CH')) {
          throw new Error(
            `Server returned invalid Twilio conversation SID "${sid}".`
          )
        }

        return sid
      } catch (error) {
        if (
          axios.isCancel(error) ||
          (error instanceof Error &&
            (error.name === 'CanceledError' || error.message === 'canceled'))
        ) {
          if (hasValidTwilioSid && existingConversationSid) {
            return existingConversationSid
          }
          throw error
        }

        if (hasValidTwilioSid && existingConversationSid) {
          return existingConversationSid
        }

        throw new Error(
          `Failed to create conversation: ${error instanceof Error ? error.message : String(error)
          }`
        )
      }
    }
  }, [selectedConversation, currentUserId])

  const handleSelectConversation = (conversation: ChatConversation) => {
    setSelectedConversation(conversation)
  }

  if (!currentUserId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-slate-500">
        Please log in to start messaging.
      </div>
    )
  }

  return (
    <div className="flex h-full w-full overflow-hidden bg-[#F6F7F9]">
      {/* Conversation List Sidebar */}
      <div className="flex flex-col w-1/3 h-full overflow-hidden bg-white border-r shadow-sm border-slate-200">
        <div
          className="flex-shrink-0 px-6 py-4 border-b shadow border-slate-100"
          style={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
          }}
        >
          <h2 className="text-xl font-semibold">Messages</h2>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <ConversationList
            onSelectConversation={handleSelectConversation}
            selectedConversationSid={resolveTwilioConversationSid(
              selectedConversation?.conversationSid,
              selectedConversation?.conversationId
            )}
          />
        </div>
      </div>

      {/* Chat Window */}
      <div className="flex-1">
        {selectedConversation && tokenFetcher && conversationResolver ? (
          <ChatProvider
            key={resolveTwilioConversationSid(
              selectedConversation.conversationSid,
              selectedConversation.conversationId
            )}
            config={{
              tokenFetcher,
              conversationResolver,
              enableDebugLogging: import.meta.env.DEV,
              participantDisplayName:
                participantDisplayName ?? participantFallbackLabel,
              participantAvatarUrl: participantAvatarUrl,
              currentUserDisplayName,
            }}
          >
            <ChatWindow />
          </ChatProvider>
        ) : (
          <div className="flex items-center justify-center h-full bg-white">
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-700">
                Select a conversation
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Choose a conversation from the list to start messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MessagingPage
